/**
 * ManagerConsolePage — קונסולת המנהל (DIGEMP-MANAGER-CONSOLE-FRONTEND-074).
 *
 * One operator workflow: a general request is entered, the eligible Digital Employees are
 * compared side by side, the reasoning behind each candidate is readable in plain Hebrew, and
 * the operator continues with a chosen employee through the EXISTING conversation flow.
 *
 * The console itself is read-only with respect to routing: it assigns nothing, starts nothing
 * and sends nothing. The match it presents is a transparent, deterministic comparison of the
 * request's words against each employee's declared specialties plus the existing fleet
 * projections (present-tense availability/load and windowed measured-outcome evidence from
 * GET /api/agents/workforce-overview). It never claims to be a model recommendation and never
 * renders raw routing logs, JSON payloads or tool output.
 *
 * Truthfulness rules:
 *   - A requested employee whose fleet projection is missing/unloaded shows "לא ידוע" for
 *     load/evidence — never 0, never "free", never an invented success rate.
 *   - With no measured outcome evidence the cell reads "טרם נמדד", not 0% (the backend's own
 *     rule: has_outcome_evidence=false and a null rate).
 *   - With no declared-specialty overlap the pool is shown unranked, clearly marked as such —
 *     no score is invented to fill the gap.
 *   - A failed handoff-history fetch says so instead of "no history".
 *   - Source states use the shared vocabulary (DIGEMP-FINISH-OPERATIONS-UX-149): while the roster
 *     is loading the console says so (never a fabricated "0 employees" or an empty table); a failed
 *     refresh keeps the last successful load and labels it as not-current; offline is stated; every
 *     error banner carries a retry.
 *
 * Permission gates (existing grants only, nothing re-implemented): viewing is agents_view;
 * opening a conversation is agents_run (the same grant the conversations API enforces). The
 * employee workspace link needs no extra grant beyond agents_view.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, companiesApi, conversationsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import { classifySource, isSourceStale, SOURCE_STATES } from "../components/statusVocabulary";
import { useOnlineStatus } from "../components/useLiveState";
import RoleSurface from "../components/designSystem/RoleSurface";
import {
  RESPONSIVE_SHELL_CLS,
  RESPONSIVE_TABLE_CLS,
  SCROLL_REGION_CLS,
} from "../components/designSystem/designTokens";
import { useViewportTier } from "../components/designSystem/useViewportTier";
import ManagerOperationsPanel from "../components/ManagerOperationsPanel";
import { detailText } from "../utils/apiErrors";

// Mirrors the request bound the routing contracts use (1..2000 chars).
const REQUEST_MAX_LENGTH = 2000;
// Bounded scan for the prior-handoff evidence: how many of the employee's conversations to look
// through when matching a selected customer. When the cap is reached the count says "at least".
const HANDOFF_SCAN_LIMIT = 50;
const EVIDENCE_WINDOW_DAYS = 7;

const TOKEN_RE = /[0-9a-z\u0590-\u05FF]+/g;
// Hebrew attaches one-letter prefixes (ב/ל/ה/מ/ש/ו/כ) to words; matching also offers the
// stripped variant so "בeset" or "ללקוח" still hits the declared term.
const HEBREW_PREFIXES = "בלהמשוכ";

const STATUS_ORDER = { available: 0, busy: 1, away: 2, offline: 3 };

export function statusLabel(status) {
  return STATUS_META[status]?.label || status || "לא ידוע";
}

export function tokensOf(text) {
  const raw = String(text || "").toLowerCase().match(TOKEN_RE) || [];
  return raw.filter((token) => token.length >= 2);
}

export function requestTokenSet(text) {
  const set = new Set();
  for (const token of tokensOf(text)) {
    set.add(token);
    if (token.length > 3 && HEBREW_PREFIXES.includes(token[0])) set.add(token.slice(1));
  }
  return set;
}

export function declaredSpecialties(agent) {
  const raw = Array.isArray(agent?.specialties) ? agent.specialties : [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const trimmed = item.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

export function matchSpecialty(specialty, normalizedRequest, requestTokens) {
  const norm = String(specialty || "").toLowerCase().trim();
  if (!norm) return false;
  if (norm.length >= 3 && normalizedRequest.includes(norm)) return true;
  return tokensOf(norm).some((token) => requestTokens.has(token));
}

const heActiveRuns = (n) => (n === 1 ? "ריצה פעילה אחת" : `${n} ריצות פעילות`);

function loadOf(candidate) {
  const active = candidate.workload?.active_runs;
  return Number.isFinite(active) ? active : Number.MAX_SAFE_INTEGER;
}

/**
 * Builds the comparable candidate list for a request. Pure and deterministic so the sorting and
 * the reasons are exactly what the operator sees; exported for focused tests.
 *
 * overviewById: Map(agentId -> workforce-overview employee entry) or null when the projection
 * failed to load — in that case load/evidence reasons read "לא ידוע" (never a guessed value).
 */
export function buildCandidates(agents, overviewById, request) {
  const normalizedRequest = String(request || "").toLowerCase();
  const requestTokens = requestTokenSet(request);
  const candidates = [];

  for (const agent of agents || []) {
    if (!agent?.is_enabled) continue;
    const overview = overviewById ? overviewById.get(agent.id) || null : null;
    const declaredStatus = agent.availability_status || "available";
    const effectiveStatus = overview?.effective_status || declaredStatus;
    if (effectiveStatus === "offline") continue;

    const specialties = declaredSpecialties(agent);
    const matchedSpecialties = specialties.filter((s) => matchSpecialty(s, normalizedRequest, requestTokens));
    const workload = overview?.workload || null;
    const readiness = overview?.specialist_readiness || null;
    const evidence = readiness
      ? {
          has: readiness.has_outcome_evidence === true,
          terminalRuns: readiness.evidence?.terminal_runs ?? null,
          verified: readiness.evidence?.verified_successes ?? null,
          rate: typeof readiness.verified_success_rate === "number" ? readiness.verified_success_rate : null,
        }
      : null;

    const candidate = {
      id: agent.id,
      name: agent.name,
      roleTitle: agent.role_title || null,
      avatarKey: agent.avatar_key ?? null,
      avatarColor: agent.avatar_color ?? null,
      specialties,
      matchedSpecialties,
      declaredStatus,
      effectiveStatus,
      overviewLoaded: overview != null,
      workload,
      evidence,
      attentionCount: typeof overview?.attention_count === "number" ? overview.attention_count : null,
      currentWork: overview?.current_work || null,
      score: matchedSpecialties.length,
    };
    candidate.reasons = buildReasons(candidate);
    candidates.push(candidate);
  }

  candidates.sort(
    (a, b) =>
      b.score - a.score ||
      (STATUS_ORDER[a.effectiveStatus] ?? 9) - (STATUS_ORDER[b.effectiveStatus] ?? 9) ||
      loadOf(a) - loadOf(b) ||
      String(a.name).localeCompare(String(b.name), "he")
  );
  return candidates;
}

/** The plain-language reasoning shown for a candidate — one fact per line, no invented facts. */
export function buildReasons(candidate) {
  const reasons = [];

  if (candidate.matchedSpecialties.length > 0) {
    reasons.push({
      kind: "match",
      text: `התמחות מוצהרת תואמת לבקשה: ${candidate.matchedSpecialties.join(", ")}`,
    });
  } else {
    reasons.push({ kind: "match", text: "לא נמצאה התאמה לתחומי התמחות מוצהרים בבקשה" });
  }

  let availability = `זמינות נוכחית: ${statusLabel(candidate.effectiveStatus)}`;
  if (candidate.effectiveStatus !== candidate.declaredStatus) {
    availability += ` (הצהרת העובד: ${statusLabel(candidate.declaredStatus)})`;
  }
  reasons.push({ kind: "availability", text: availability });

  if (!candidate.overviewLoaded) {
    reasons.push({ kind: "load", text: "עומס נוכחי: לא ידוע — נתוני מצבת העובדים אינם זמינים כרגע" });
    reasons.push({ kind: "evidence", text: "עדות נמדדת: לא ידוע — נתוני מצבת העובדים אינם זמינים כרגע" });
  } else {
    const w = candidate.workload;
    if (!w || w.active_runs == null) {
      reasons.push({ kind: "load", text: "עומס נוכחי: לא ידוע — נתוני העומס אינם זמינים עבור עובד זה" });
    } else if (w.active_runs === 0) {
      reasons.push({ kind: "load", text: "עומס נוכחי: אין ריצות פעילות" });
    } else {
      const parts = [heActiveRuns(w.active_runs)];
      if (w.waiting_for_customer > 0) {
        parts.push(w.waiting_for_customer === 1 ? "אחת ממתינה לתשובת לקוח" : `${w.waiting_for_customer} ממתינות לתשובת לקוח`);
      }
      if (w.paused > 0) parts.push(w.paused === 1 ? "אחת מושהית" : `${w.paused} מושהות`);
      reasons.push({ kind: "load", text: `עומס נוכחי: ${parts.join(" · ")}` });
    }

    const ev = candidate.evidence;
    if (!ev) {
      reasons.push({
        kind: "evidence",
        text: "עדות נמדדת: לא ידוע — נתוני העדויות אינם זמינים עבור עובד זה",
      });
    } else if (ev.has) {
      const pct = ev.rate != null ? ` — ${Math.round(ev.rate * 100)}% הצלחה מאומתת` : "";
      reasons.push({
        kind: "evidence",
        text: `עדות נמדדת: ${ev.verified} מתוך ${ev.terminalRuns} ריצות שהסתיימו אומתו כהצלחה${pct}`,
      });
    } else {
      reasons.push({
        kind: "evidence",
        text: `עדות נמדדת: טרם נמדד — אין ריצות שהסתיימו בחלון של ${EVIDENCE_WINDOW_DAYS} הימים האחרונים`,
      });
    }
  }

  if (candidate.attentionCount > 0) {
    reasons.push({
      kind: "attention",
      text: `דורש תשומת לב: ${candidate.attentionCount === 1 ? "פריט אחד ממתין" : `${candidate.attentionCount} פריטים ממתינים`} לטיפול אנושי`,
    });
  }

  return reasons;
}

export function formatHandoffDate(iso) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
  } catch {
    return null;
  }
}

// ─── Building blocks ─────────────────────────────────────────────────────────

function EvidenceCell({ candidate }) {
  if (!candidate.overviewLoaded || !candidate.evidence) {
    return <span className="text-gray-400 italic">לא ידוע</span>;
  }
  const ev = candidate.evidence;
  if (!ev?.has) {
    return <span className="text-gray-400 italic">טרם נמדד</span>;
  }
  return (
    <span>
      {ev.verified}/{ev.terminalRuns} אומתו
      {ev.rate != null && <span className="text-gray-400"> · {Math.round(ev.rate * 100)}%</span>}
    </span>
  );
}

function StatusCell({ candidate }) {
  return (
    <span>
      {statusLabel(candidate.effectiveStatus)}
      {candidate.effectiveStatus !== candidate.declaredStatus && (
        <span className="block text-[11px] text-gray-400">הצהרה: {statusLabel(candidate.declaredStatus)}</span>
      )}
    </span>
  );
}

function CandidateRow({ candidate, isSelected, onSelect }) {
  return (
    <tr className={`border-t border-gray-100 ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"}`}>
      <td className="px-3 py-2.5" data-label="עובד">
        <div className="flex items-center gap-2.5 min-w-0">
          <EmployeeAvatar
            name={candidate.name}
            avatarKey={candidate.avatarKey}
            avatarColor={candidate.avatarColor}
            status={candidate.effectiveStatus}
            size="sm"
            showStatus
          />
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => onSelect(candidate.id)}
              aria-pressed={isSelected}
              aria-label={`בחר את ${candidate.name}`}
              className={`text-sm font-medium text-right hover:underline ${
                isSelected ? "text-blue-800" : "text-gray-800"
              }`}
            >
              {candidate.name}
            </button>
            {candidate.roleTitle && <p className="text-[11px] text-gray-500 truncate">{candidate.roleTitle}</p>}
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap" data-label="זמינות">
        <StatusCell candidate={candidate} />
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap" data-label="עומס פעיל">
        {!candidate.overviewLoaded || candidate.workload?.active_runs == null ? (
          <span className="text-gray-400 italic">לא ידוע</span>
        ) : candidate.workload.active_runs > 0 ? (
          heActiveRuns(candidate.workload.active_runs)
        ) : (
          "אין ריצות פעילות"
        )}
      </td>
      <td className="px-3 py-2.5" data-label="התאמות מוצהרות">
        {candidate.matchedSpecialties.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {candidate.matchedSpecialties.map((s) => (
              <span
                key={s}
                className="text-[11px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200"
              >
                {s}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400 italic">אין התאמה מוצהרת</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap" data-label={`עדות נמדדת (${EVIDENCE_WINDOW_DAYS} ימים)`}>
        <EvidenceCell candidate={candidate} />
      </td>
    </tr>
  );
}

function CandidateTable({ candidates, selectedId, onSelect, label }) {
  // Below 640px the comparison becomes labelled cards (responsive.css); on tablet/desktop the
  // table scrolls sideways, so it must be a keyboard focus stop with an accessible name.
  const viewportTier = useViewportTier();
  const scrollRegion = viewportTier !== "mobile";
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border overflow-x-auto ${RESPONSIVE_SHELL_CLS}${
        scrollRegion ? ` ${SCROLL_REGION_CLS}` : ""
      }`}
      role={scrollRegion ? "region" : undefined}
      aria-label={scrollRegion ? `${label} — ניתן לגלילה אופקית` : undefined}
      tabIndex={scrollRegion ? 0 : undefined}
    >
      <table className={`w-full text-sm ${RESPONSIVE_TABLE_CLS}`} aria-label={label}>
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs">
            <th scope="col" className="text-right px-3 py-2 font-medium">עובד</th>
            <th scope="col" className="text-right px-3 py-2 font-medium">זמינות</th>
            <th scope="col" className="text-right px-3 py-2 font-medium">עומס פעיל</th>
            <th scope="col" className="text-right px-3 py-2 font-medium">התאמות מוצהרות</th>
            <th scope="col" className="text-right px-3 py-2 font-medium">עדות נמדדת ({EVIDENCE_WINDOW_DAYS} ימים)</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate) => (
            <CandidateRow
              key={candidate.id}
              candidate={candidate}
              isSelected={candidate.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HandoffEvidence({ state, customerSelected }) {
  if (!customerSelected) {
    return (
      <p className="text-xs text-gray-400">
        בחרו לקוח כדי לבדוק אם העובד כבר טיפל בפניות של לקוח זה.
      </p>
    );
  }
  if (state.loading) return <p className="text-xs text-gray-400">בודק היסטוריית שיחות עם הלקוח…</p>;
  if (state.error) {
    return <p className="text-xs text-amber-700">היסטוריית השיחות לא נטענה — המצב אינו ידוע.</p>;
  }
  if (state.count == null) return null;
  if (state.count === 0) {
    return <p className="text-xs text-gray-500">לא נמצאו שיחות קודמות עם לקוח זה.</p>;
  }
  const last = state.lastUpdated ? ` · אחרונה: ${formatHandoffDate(state.lastUpdated)}` : "";
  if (state.count === 1) {
    return <p className="text-xs text-gray-600">נמצאה שיחה קודמת אחת עם לקוח זה{last}.</p>;
  }
  const atLeast = state.count >= HANDOFF_SCAN_LIMIT ? "לפחות " : "";
  return (
    <p className="text-xs text-gray-600">
      נמצאו {atLeast}
      {state.count} שיחות קודמות עם לקוח זה{last}.
    </p>
  );
}

function CandidateDetail({ candidate, canRun, starting, actionError, handoff, customerSelected, onStart }) {
  if (!candidate) {
    return (
      <section aria-label="פרטי המועמד הנבחר" className="bg-white rounded-xl shadow-sm border p-5">
        <p className="text-sm text-gray-400">בחרו מועמד כדי לקרוא את נימוקי ההתאמה ולהמשיך איתו.</p>
      </section>
    );
  }

  return (
    <section aria-label="פרטי המועמד הנבחר" className="bg-white rounded-xl shadow-sm border p-5 space-y-4">
      <div className="flex items-center gap-3">
        <EmployeeAvatar
          name={candidate.name}
          avatarKey={candidate.avatarKey}
          avatarColor={candidate.avatarColor}
          status={candidate.effectiveStatus}
          size="md"
          showStatus
        />
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-800">{candidate.name}</h3>
          {candidate.roleTitle && <p className="text-xs text-gray-500">{candidate.roleTitle}</p>}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-500 mb-1.5">נימוקי ההתאמה</h4>
        <ul className="space-y-1 text-sm text-gray-700">
          {candidate.reasons.map((reason, i) => (
            <li key={`${reason.kind}-${i}`} className="flex gap-1.5">
              <span aria-hidden="true" className="text-gray-400">•</span>
              <span>{reason.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {candidate.currentWork && (
        <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 text-sm">
          <span className="text-gray-500">עובד כעת:</span>{" "}
          {candidate.currentWork.ticket_id ? (
            <Link to={`/tickets/${candidate.currentWork.ticket_id}`} className="text-blue-700 hover:underline">
              {candidate.currentWork.ticket_number != null
                ? `קריאה #${candidate.currentWork.ticket_number}`
                : "קריאה משויכת"}
              {candidate.currentWork.ticket_title ? ` — ${candidate.currentWork.ticket_title}` : ""}
            </Link>
          ) : (
            <Link to={`/agent-runs/${candidate.currentWork.run_id}`} className="text-blue-700 hover:underline">
              ריצה פעילה
            </Link>
          )}
        </div>
      )}

      <HandoffEvidence state={handoff} customerSelected={customerSelected} />

      {actionError && (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {actionError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
        {canRun ? (
          <button
            type="button"
            onClick={onStart}
            disabled={starting}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {starting ? "פותח שיחה…" : `התחל שיחה עם ${candidate.name}`}
          </button>
        ) : (
          <p className="text-xs text-amber-700">
            אין לך הרשאת הפעלת עובדים — לא ניתן לפתוח שיחה מכאן. אפשר לפתוח את מרחב העבודה של העובד.
          </p>
        )}
        <Link
          to={`/agents/${candidate.id}`}
          className="text-sm text-blue-700 hover:underline"
        >
          פתח את מרחב העבודה של העובד ←
        </Link>
        {canRun && (
          <span className="text-xs text-gray-400">הבקשה תיפתח כטיוטה בשיחה — דבר לא נשלח עד שתאשרו.</span>
        )}
      </div>
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ManagerConsolePage() {
  const { can: canAgent, loading: permsLoading } = useAgentPermissions();
  const canView = canAgent("agents_view");
  const canRun = canAgent("agents_run");
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();

  const [request, setRequest] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const agentsQuery = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    enabled: canView,
    staleTime: 30_000,
  });

  const overviewQuery = useQuery({
    queryKey: ["workforce-overview"],
    queryFn: () => agentsApi.workforceOverview(),
    enabled: canView,
    staleTime: 30_000,
  });

  const companiesQuery = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
    enabled: canView,
    staleTime: 60_000,
  });

  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const companies = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);
  const companiesFailed = companiesQuery.isError && !companiesQuery.data;

  const overviewById = useMemo(() => {
    if (!overviewQuery.data) return null;
    const map = new Map();
    for (const entry of overviewQuery.data.employees ?? []) map.set(entry.agent_id, entry);
    return map;
  }, [overviewQuery.data]);

  // Scoped to the selected customer the same way the ticket flow scopes its agent picker.
  const scopedAgents = useMemo(() => {
    if (!customerId) return agents;
    return agents.filter((agent) => !agent.client_id || agent.client_id === customerId);
  }, [agents, customerId]);

  const candidates = useMemo(
    () => buildCandidates(scopedAgents, overviewById, request),
    [scopedAgents, overviewById, request]
  );
  const ranked = candidates.filter((c) => c.score > 0);
  const unranked = candidates.filter((c) => c.score === 0);
  const selected = candidates.find((c) => c.id === selectedId) || null;

  const handoffQuery = useQuery({
    queryKey: ["manager-console-handoffs", selectedId, customerId],
    queryFn: () => conversationsApi.list({ agent_id: selectedId, limit: HANDOFF_SCAN_LIMIT }),
    enabled: canView && !!selectedId && !!customerId,
    staleTime: 60_000,
  });

  const handoffState = useMemo(() => {
    if (!customerId || !selectedId) return { count: null, loading: false, error: false, lastUpdated: null };
    if (handoffQuery.isError && !handoffQuery.data) {
      return { count: null, loading: false, error: true, lastUpdated: null };
    }
    if (!handoffQuery.data) return { count: null, loading: true, error: false, lastUpdated: null };
    const matching = handoffQuery.data.filter((c) => c.customer_id === customerId);
    const lastUpdated = matching
      .map((c) => c.updated_at)
      .filter(Boolean)
      .sort()
      .pop() || null;
    return { count: matching.length, loading: false, error: false, lastUpdated };
  }, [handoffQuery.data, handoffQuery.isError, customerId, selectedId]);

  const trimmedRequest = request.trim();
  const canSuggest = trimmedRequest.length > 0;

  if (permsLoading) {
    return <div className="p-6 text-gray-500">טוען הרשאות…</div>;
  }
  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בקונסולת המנהל</div>;
  }

  const startConversation = async () => {
    if (!selected || !canRun || starting) return;
    setStarting(true);
    setActionError(null);
    try {
      const conversation = await conversationsApi.create({
        customer_id: customerId || undefined,
        target: { kind: "agent", agent_id: selected.id },
      });
      navigate(`/conversations/${conversation.id}`, {
        state: { managerConsoleRequest: trimmedRequest },
      });
    } catch (err) {
      setActionError(
        detailText(err?.response?.data?.detail, "לא ניתן לפתוח שיחה כעת — הבקשה לא נשלחה.")
      );
      setStarting(false);
    }
  };

  const agentsSourceState = classifySource(agentsQuery);
  const agentsFailed = agentsSourceState.key === SOURCE_STATES.failed;
  const agentsLoading = agentsSourceState.key === SOURCE_STATES.loading;
  // The roster and the fleet projection feed the same table; a refresh failure of either source
  // means the shown availability/load/evidence may be the last successful load, not the present.
  const agentsStale = agentsSourceState.key === SOURCE_STATES.stale || isSourceStale(overviewQuery);
  const overviewFailed = overviewQuery.isError && !overviewQuery.data;
  const newestLoadedAt = Math.max(agentsQuery.dataUpdatedAt || 0, overviewQuery.dataUpdatedAt || 0);
  const newestLoaded = newestLoadedAt > 0 ? new Date(newestLoadedAt).toLocaleTimeString("he-IL") : null;
  const disabledCount = agents.filter((a) => !a.is_enabled).length;

  return (
    <RoleSurface
      role="manager"
      dir="rtl"
      title="קונסולת המנהל"
      description="תארו בקשה כללית, השוו בין העובדים הדיגיטליים הזמינים והמשיכו עם העובד שתבחרו — בשפה אנושית, בלי לוגים גולמיים."
      actions={
        <button
          type="button"
          onClick={() => {
            agentsQuery.refetch();
            overviewQuery.refetch();
          }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
        >
          רענן
        </button>
      }
      testId="manager-role-surface"
    >

      <section aria-label="הבקשה" className="bg-white rounded-xl shadow-sm border p-4 space-y-3">
        <div>
          <label htmlFor="manager-console-request" className="block text-xs text-gray-500 mb-1">
            תיאור הבקשה
          </label>
          <textarea
            id="manager-console-request"
            rows={3}
            maxLength={REQUEST_MAX_LENGTH}
            value={request}
            onChange={(e) => setRequest(e.target.value)}
            placeholder="לדוגמה: לקוח צריך עזרה עם ESET בשרת — יש לבדוק חסימות וסריקות."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="manager-console-customer" className="text-xs text-gray-500">
            לקוח (אופציונלי)
          </label>
          <select
            id="manager-console-customer"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setSelectedId(null);
            }}
            className="text-sm border border-gray-200 rounded px-2 py-1.5"
          >
            <option value="">ללא שיוך ללקוח</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {companiesFailed && (
            <span className="text-xs text-amber-700">רשימת הלקוחות לא נטענה — אין שיוך ללקוח כרגע.</span>
          )}
        </div>
        <p role="note" className="text-xs text-gray-500">
          ההתאמה היא המלצה בלבד על בסיס הנתונים המוצגים — היא אינה הקצאה, אינה מפעילה ריצה ואינה שולחת
          הודעה. ההחלטה וההמשך הם של המפעיל.
        </p>
      </section>

      <ManagerOperationsPanel
        canView={canView}
        companyId={customerId}
        companyName={companies.find((c) => c.id === customerId)?.name || null}
      />

      {!isOnline && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3">
          {agentsQuery.data != null
            ? "אין חיבור לרשת — נתוני העובדים לא מתרעננים כרגע; מוצג הנתון מהטעינה האחרונה שהצליחה."
            : "אין חיבור לרשת — לא ניתן לטעון את נתוני העובדים כרגע."}
        </p>
      )}

      {agentsFailed ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm p-4 flex flex-wrap items-center justify-between gap-3">
          <span>לא ניתן לטעון את סגל העובדים הדיגיטליים — לא ניתן להציג מועמדים כרגע.</span>
          <button
            type="button"
            onClick={() => agentsQuery.refetch()}
            className="border border-red-300 rounded-lg px-3 py-1 text-xs hover:bg-red-100"
          >
            נסה שוב
          </button>
        </div>
      ) : agentsLoading ? (
        <div role="status" className="rounded-lg border border-gray-200 bg-gray-50 text-gray-500 text-sm p-4 flex items-center gap-3">
          <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-400" />
          טוען את סגל העובדים הדיגיטליים…
        </div>
      ) : (
        <>
          {agentsStale && isOnline && (
            <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3 flex flex-wrap items-center justify-between gap-3">
              <span>
                רענון נתוני העובדים נכשל — מוצג הנתון מהטעינה האחרונה שהצליחה
                {newestLoaded ? ` (${newestLoaded})` : ""}; ייתכן שהמצב אינו מעודכן.
              </span>
              <button
                type="button"
                onClick={() => {
                  agentsQuery.refetch();
                  overviewQuery.refetch();
                }}
                className="border border-amber-300 rounded-lg px-3 py-1 text-xs hover:bg-amber-100"
              >
                נסה שוב
              </button>
            </div>
          )}

          {overviewFailed && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3 flex flex-wrap items-center justify-between gap-3">
              <span>
                נתוני הזמינות הנוכחית, העומס והעדויות הנמדדות לא נטענו — ההתאמה מבוססת על תחומי ההתמחות
                המוצהרים בלבד, והשדות האלה מוצגים כ"לא ידוע".
              </span>
              <button
                type="button"
                onClick={() => overviewQuery.refetch()}
                className="border border-amber-300 rounded-lg px-3 py-1 text-xs hover:bg-amber-100"
              >
                נסה שוב
              </button>
            </div>
          )}

          {!canSuggest ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 text-gray-600 text-sm p-4">
              הקלידו תיאור בקשה כדי לראות את דירוג ההתאמה של העובדים.
            </div>
          ) : (
            <>
              <section aria-label="השוואת מועמדים" className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base font-semibold text-gray-800">
                    {ranked.length > 0 ? "מועמדים מדורגים לפי התאמה" : "מועמדים ללא דירוג"}
                  </h2>
                  <span className="text-xs text-gray-500">
                    {candidates.length} עובדים זמינים
                    {disabledCount > 0 ? ` · ${disabledCount} מושבתים אינם מוצגים` : ""}
                  </span>
                </div>

                {ranked.length === 0 && (
                  <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    לא נמצאה התאמה לתחומי התמחות מוצהרים בבקשה — מוצגים כל העובדים הזמינים ללא דירוג.
                    אפשר לבחור עובד ולהמשיך, או לחדד את הבקשה.
                  </p>
                )}

                <CandidateTable
                  candidates={ranked.length > 0 ? ranked : unranked}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  label={ranked.length > 0 ? "מועמדים מדורגים לפי התאמה" : "עובדים זמינים ללא דירוג"}
                />

                {ranked.length > 0 && unranked.length > 0 && (
                  <details className="bg-white rounded-xl border border-gray-200 p-3">
                    <summary className="text-sm text-gray-600 cursor-pointer">
                      עובדים זמינים ללא התאמת תחום מוצהרת ({unranked.length})
                    </summary>
                    <div className="mt-2">
                      <CandidateTable
                        candidates={unranked}
                        selectedId={selectedId}
                        onSelect={setSelectedId}
                        label="עובדים זמינים ללא התאמת תחום מוצהרת"
                      />
                    </div>
                  </details>
                )}
              </section>

              <CandidateDetail
                candidate={selected}
                canRun={canRun}
                starting={starting}
                actionError={actionError}
                handoff={handoffState}
                customerSelected={!!customerId}
                onStart={startConversation}
              />
            </>
          )}

          <details className="bg-white rounded-xl border border-gray-200 p-4">
            <summary className="text-sm font-medium text-gray-700 cursor-pointer">
              איך נבחרו המועמדים?
            </summary>
            <ul className="mt-2 space-y-1 text-sm text-gray-600 list-disc pr-5">
              <li>מוצגים רק עובדים פעילים שאינם מנותקים (מנותקים אינם מועמדים כלל).</li>
              <li>
                הדירוג מבוסס על חפיפת מילים מהבקשה לתחומי ההתמחות המוצהרים של העובד — כמה תחומים
                תואמים, כך גבוה יותר.
              </li>
              <li>שוברי שוויון: זמינות נוכחית, עומס פעיל נמוך יותר, ולבסוף סדר אלפביתי.</li>
              <li>
                זמינות נוכחית, עומס ועדויות מדידה מגיעים ממצבת העובדים הקיימת; עדויות נמדדות מוגבלות
                לחלון של {EVIDENCE_WINDOW_DAYS} הימים האחרונים ורק הצלחות שאומתו נספרות כהצלחות.
              </li>
              <li>אין כאן נתונים גולמיים, לוגים או פלטי כלים — כל השיקולים מוצגים בשפה אנושית.</li>
            </ul>
          </details>
        </>
      )}
    </RoleSurface>
  );
}
